import { intersection, mapValues, sum, uniq } from 'es-toolkit';
import { createSelector } from '@reduxjs/toolkit';

import { notNullish } from '@paragon/utilities';

import { HeightCache } from '../ComponentGeometryCaches';
import { RootState } from '../store';
import { getBundlesByScheduleLineGroupGuid, getBundlesByTruckloadGuid } from './bundles';
import { getDistanceFormatterSelector } from './getDistanceFormatterSelector';
import { getProductionComponentInstancesByBundleGuid } from './getProductionComponentInstancesByBundleGuid';
import { displayedScheduleLineGroupStatuses } from './lineScheduleGrid';
import {
  getBundleGuidFromRowId,
  getScheduleGroupGridRowsByScheduleGroupGuid,
  GridRow,
  isBundleRow,
} from './scheduleGroupGrid';
import { getScheduleLineGroupsByLineGuid, getStatusesByScheduleLineGroupGuid } from './scheduleLineGroups';
import { sumComponentValueForInstances } from './sumComponentValueInBundle';
import { getTruckloadsForTruckloadGrid } from './truckloadGrid';
import { getLoadedTrussesByAnalysisSetGuid, getTrussMeasurements } from './trusses';

export const getScheduleGroupGuidsForScheduleGrid = createSelector(
  getScheduleGroupGridRowsByScheduleGroupGuid,

  (scheduleGroupGridRowsByScheduleGroupGuid) =>
    Object.entries(scheduleGroupGridRowsByScheduleGroupGuid).reduce(
      (scheduleGroupGuids, [scheduleGroupGuid, rowsForScheduleGroup]) => {
        const statuses = uniq(
          rowsForScheduleGroup.flatMap((row) =>
            row.statusPercentages.map((statusPercentage) => statusPercentage.status),
          ),
        );
        const hasStatusOtherThanDelivered = statuses.some((status) => status !== 'Delivered');
        if (hasStatusOtherThanDelivered) scheduleGroupGuids.push(scheduleGroupGuid);
        return scheduleGroupGuids;
      },
      [] as string[],
    ),
);

export const getScheduleGridRows = createSelector(
  getScheduleGroupGuidsForScheduleGrid,
  getScheduleGroupGridRowsByScheduleGroupGuid,

  (scheduleGroupGuidsForScheduleGrid, scheduleGroupGridRowsByScheduleGroupGuid): readonly GridRow[] =>
    scheduleGroupGuidsForScheduleGrid.flatMap(
      (scheduleGroupGuid) => scheduleGroupGridRowsByScheduleGroupGuid[scheduleGroupGuid],
    ),
);

export const getScheduleGridCheckedBundleGuids = createSelector(
  (state: RootState) => state.scheduleGrid.checkedRows,

  (checkedRows) => checkedRows.filter(isBundleRow).map(getBundleGuidFromRowId),
);

export const getScheduleGroupsForCheckedRows = createSelector(
  getScheduleGridCheckedBundleGuids,
  (state: RootState) => state.bundles,
  (state: RootState) => state.scheduleGroups,

  (checkedBundleGuids, bundles, scheduleGroups) =>
    uniq(checkedBundleGuids.map((bundleGuid) => bundles[bundleGuid].scheduleGroupGuid))
      .filter(notNullish)
      .map((scheduleGroupGuid) => scheduleGroups[scheduleGroupGuid]),
);

export const getActiveScheduleLineGroupsByLineGuid = createSelector(
  (state: RootState) => state.lines,
  getScheduleLineGroupsByLineGuid,
  getStatusesByScheduleLineGroupGuid,

  (lines, scheduleLineGroupsByLineGuid, statusesByScheduleLineGroupGuid) =>
    mapValues(lines, (line) =>
      scheduleLineGroupsByLineGuid[line.guid].filter((scheduleLineGroup) =>
        statusesByScheduleLineGroupGuid[scheduleLineGroup.guid].length > 0
          ? intersection(statusesByScheduleLineGroupGuid[scheduleLineGroup.guid], displayedScheduleLineGroupStatuses)
              .length > 0
          : true,
      ),
    ),
);

export const getActiveScheduleLineGroupsForCheckedBundlesWithSameScheduleGroupByLineGuid = createSelector(
  getScheduleGroupsForCheckedRows,
  getActiveScheduleLineGroupsByLineGuid,
  getScheduleLineGroupsByLineGuid,
  getBundlesByScheduleLineGroupGuid,

  (
    scheduleGroupsForCheckedRows,
    activeScheduleLineGroupsByLineGuid,
    scheduleLineGroupsByLineGuid,
    bundlesByScheduleLineGroupGuid,
  ) =>
    mapValues(activeScheduleLineGroupsByLineGuid, (activeScheduleLineGroupsForLine) =>
      activeScheduleLineGroupsForLine.filter((scheduleLineGroup) => {
        // Generally we expect a ScheduleLineGroup to be associated with one ScheduleGroup, but this code does handle
        // the plural case
        const scheduleGroupGuidsForScheduleLineGroup = uniq(
          bundlesByScheduleLineGroupGuid[scheduleLineGroup.guid]
            .map((bundle) => bundle.scheduleGroupGuid)
            .filter(notNullish),
        );
        return (
          scheduleGroupGuidsForScheduleLineGroup.length === 0 ||
          scheduleGroupsForCheckedRows.every((scheduleGroup) =>
            scheduleGroupGuidsForScheduleLineGroup.includes(scheduleGroup.guid),
          )
        );
      }),
    ),
);

export const getActiveTruckloadsForCheckedBundlesWithSameProject = createSelector(
  getScheduleGridCheckedBundleGuids,
  (state: RootState) => state.bundles,
  (state: RootState) => state.productionGroups,
  getTruckloadsForTruckloadGrid,
  getBundlesByTruckloadGuid,

  (checkedBundleGuids, bundles, productionGroups, truckloadsForTruckloadGrid, bundlesByTruckloadGuid) => {
    function getProjectGuidsForBundleGuids(bundleGuids: readonly string[]) {
      const productionGroupGuidsForBundles = uniq(
        bundleGuids.map((bundleGuid) => bundles[bundleGuid].productionGroupGuid).filter(notNullish),
      );
      const projectGuidsForBundles = uniq(
        productionGroupGuidsForBundles.map((productionGroupGuid) => productionGroups[productionGroupGuid].projectGuid),
      );
      return projectGuidsForBundles;
    }

    const projectGuidsForCheckedBundles = getProjectGuidsForBundleGuids(checkedBundleGuids);

    return truckloadsForTruckloadGrid.filter((truckload) => {
      const bundleGuidsForTruckload = bundlesByTruckloadGuid[truckload.guid].map((bundle) => bundle.guid);
      if (bundleGuidsForTruckload.length === 0) return true;
      const projectGuidsForTruckloadBundles = getProjectGuidsForBundleGuids(bundleGuidsForTruckload);
      return intersection(projectGuidsForTruckloadBundles, projectGuidsForCheckedBundles).length > 0;
    });
  },
);

export const getScheduleCheckedRowTotals = createSelector(
  getScheduleGridCheckedBundleGuids,
  getProductionComponentInstancesByBundleGuid,
  getTrussMeasurements,
  getLoadedTrussesByAnalysisSetGuid,
  getDistanceFormatterSelector,

  (
    checkedBundleGuids,
    productionComponentInstancesByBundleGuid,
    trussMeasurements,
    loadedTrussesByAnalysisSetGuid,
    distanceFormatter,
  ) => {
    const checkedTrussCountTotal = sum(
      checkedBundleGuids.map((bundleGuid) => productionComponentInstancesByBundleGuid[bundleGuid].length),
    );
    const checkedBoardFeetTotal = `${Math.round(
      sum(
        checkedBundleGuids.map((bundleGuid) =>
          sumComponentValueForInstances(
            productionComponentInstancesByBundleGuid[bundleGuid],
            trussMeasurements,
            (summary) => summary?.boardFeet ?? 0,
          ),
        ),
      ),
    )} BF`;
    const checkedLinearFeetTotal = `${Math.round(
      sum(
        checkedBundleGuids.map((bundleGuid) =>
          sumComponentValueForInstances(
            productionComponentInstancesByBundleGuid[bundleGuid],
            trussMeasurements,
            (summary) => summary?.linearFeet ?? 0,
          ),
        ),
      ),
    )} LF`;
    const uniqueThicknesses = uniq(
      checkedBundleGuids.flatMap((bundleGuid) =>
        productionComponentInstancesByBundleGuid[bundleGuid]
          .map((instance) => loadedTrussesByAnalysisSetGuid[instance.analysisSetGuid]?.thickness)
          .filter(notNullish),
      ),
    );
    const thicknesses =
      uniqueThicknesses.length === 0
        ? null
        : uniqueThicknesses.length > 1
          ? 'Mixed'
          : distanceFormatter(uniqueThicknesses[0]);
    const uniqueHeights = uniq(
      checkedBundleGuids.flatMap((bundleGuid) =>
        productionComponentInstancesByBundleGuid[bundleGuid]
          .map((instance) =>
            instance.analysisSetGuid in loadedTrussesByAnalysisSetGuid
              ? HeightCache.get(instance.analysisSetGuid, loadedTrussesByAnalysisSetGuid[instance.analysisSetGuid])
              : null,
          )
          .filter(notNullish),
      ),
    );
    const heights =
      uniqueHeights.length === 0 ? null : uniqueHeights.length > 1 ? 'Mixed' : distanceFormatter(uniqueHeights[0]);

    return { checkedTrussCountTotal, checkedBoardFeetTotal, checkedLinearFeetTotal, thicknesses, heights };
  },
);
